import type { ParseKeys } from 'i18next'
import { useMemo } from 'react'
import useSWR from 'swr'
import { WorkflowTemplateSummary } from '@digital-twins/shared-types'

import i18n from '@/i18n'

import { workflowDeliverySettingsApi, workflowsApi } from '@/lib/api'
import {
  THREAD_ACTION_SLUG_PREFIX,
  templateSurface,
} from '@/lib/workflows/templateSurface'

export { THREAD_ACTION_SLUG_PREFIX }

/** True for a command template (`/test`, `/report`), never a lobby workflow. */
export function isThreadActionSlug(slug: string | null | undefined): boolean {
  return typeof slug === 'string' && templateSurface({ slug }) === 'thread'
}

/**
 * One command a thread can hand to a workflow: the name a reader types, plus
 * the product copy the confirm screen and its toast read out.
 *
 * `slug` and `token` are the template's own; everything else is either curated
 * here or derived from the template's metadata, so a surface rendering a
 * command never has to touch the summary again.
 */
export type ThreadCommandKind = 'workflow' | 'report'

/** Which intake prompt a command's scoping beat runs under (report mode, D12). */
export type ThreadCommandIntakeKind = 'twin_test' | 'report'

export interface ThreadCommand {
  slug: string
  /**
   * `workflow_templates.token`, for `getTemplate` and `workflowRunsApi.create`.
   * Empty string on a NATIVE command (`kind: 'report'`), which has no template
   * record at all: nothing may resolve or start a run from it.
   */
  token: string
  /** What the reader would type. The menu row and the typed form are one thing. */
  command: string
  /**
   * Older spellings that still run this command. The menu and settings show
   * `command`; these are accepted when typed and are not listed.
   */
  legacyCommands?: Array<string>
  label: string
  /** One line in the confirm step saying what running it is about to spend. */
  blurb: string
  /** The confirm step's button. Names the act, never a bare "Confirm". */
  confirmLabel: string
  /**
   * Catalogue fallback from `GET /workflows` (`description`). The slash
   * list replaces this with the attached skill body once it lands —
   * never the step prompt (JSON / SurveyJS contracts).
   */
  prompt: string
  /** Success toast. Per-command so the copy reads as a sentence. */
  startedMessage: string
  /**
   * True for a command whose confirm step ARMS the composer instead of
   * starting a run on the spot (twin-test conversational intake, Step 4).
   * Carried on the resolved `ThreadCommand`, not just the alias table, so a
   * consumer like `ThreadCommandMenu` can branch on it without reaching back
   * into this file's private map.
   */
  arms: boolean
  /**
   * `'workflow'` hands the command to `POST /workflow-runs`; `'report'` is a
   * NATIVE command that resolves to an assistant turn in report mode (D1) and
   * must never reach `workflowRunsApi.create`. Every template-derived command
   * is `'workflow'`.
   */
  kind: ThreadCommandKind
  /**
   * True when the armed send goes to conversational INTAKE (`/test`, and
   * `/report`, whose intake is the scoping beat) rather than straight to a
   * run. Every other armed command starts on send.
   */
  intake?: boolean
  /**
   * Which intake prompt the scoping beat runs under (D12). Left UNDEFINED for
   * the twin-test command on purpose: the request omits `intake.kind` entirely
   * so twin-test bytes stay identical to what ships today.
   */
  intakeKind?: ThreadCommandIntakeKind
}

/**
 * The curated half of a command: product copy that is not template metadata,
 * and an optional name that beats the one derived from the slug.
 */
interface ThreadCommandAlias {
  /**
   * Overrides the derived `/`+slug name. Only present where the derived name
   * would be worse than the one the product already ships.
   */
  command?: string
  /** Older spellings that still run the command. Not shown in the menu. */
  legacyCommands?: Array<string>
  labelKey: ParseKeys<'app'>
  blurbKey: ParseKeys<'app'> | null
  confirmKey: ParseKeys<'app'> | ParseKeys<'common'>
  confirmNs?: 'app' | 'common'
  startedKey: ParseKeys<'app'>
  /**
   * See `ThreadCommand.arms`. Optional here because only `/test` and
   * `/report` set it; every other alias defaults to `false` in
   * `toThreadCommand`.
   */
  arms?: boolean
  /** See `ThreadCommand.intake`. */
  intake?: boolean
  /**
   * D5: whether this command is offered off-thread, before a thread exists
   * (Home Lab, `/threads`). Only `/test` sets it — a workflow that starts on
   * the spot has nowhere to run until the thread page is there to dispatch
   * it, so an off-thread composer only ever hands off the conversational
   * intake command. Not carried onto the resolved `ThreadCommand`: it is
   * only ever read by `useThreadCommands` itself to filter its own list.
   */
  offThread?: boolean
}

/**
 * Copy for the seeded commands, keyed by slug, IN THE ORDER THEY READ.
 *
 * The command NAME is derived from the slug (`thread-action-report` →
 * `/report`), so an org that seeds a fourth `thread-action-*` template gets it
 * in the menu and in the typed form for free. This table only carries what a
 * template cannot: the copy is product voice rather than the operator-facing
 * `displayName`/`description` the catalogue stores, and `/test` is a name the
 * derivation would never produce from `thread-action-twin-test`.
 *
 * Insertion order is the on-screen order for these three; anything unaliased
 * follows in the order `GET /workflows` returned it. Deriving the order purely
 * from the response would let a change in the backend's list ordering silently
 * reshuffle a menu readers have learned.
 */
const THREAD_COMMAND_ALIASES: ReadonlyMap<string, ThreadCommandAlias> = new Map([
  [
    'thread-action-twin-test',
    {
      command: '/test',
      labelKey: 'threads.detail.command.twinTest.label',
      /* Empty: the template's first remaining field label ("Which audience
         should answer?") is already the lead. A second sentence stacked
         above it restated the same beat. */
      blurbKey: null,
      confirmKey: 'continue',
      confirmNs: 'common',
      startedKey: 'threads.detail.command.twinTest.started',
      /**
       * `/test` ARMS the composer. Confirm collects the audience; the ask is
       * written or edited in the composer, then send drafts questions. Plan
       * Start uses the same gate — never start the run on Confirm.
       */
      arms: true,
      intake: true,
      offThread: true,
    },
  ],
  [
    'thread-action-copywrite',
    {
      command: '/copywriting',
      legacyCommands: ['/copywrite'],
      labelKey: 'threads.detail.command.copywrite.label',
      blurbKey: 'threads.detail.command.copywrite.blurb',
      confirmKey: 'threads.detail.command.copywrite.confirm',
      startedKey: 'threads.detail.command.copywrite.started',
    },
  ],
  [
    'thread-action-report',
    /**
     * INERT since D10: the seeded row this alias describes is filtered out of
     * the list below and the native `/report` is built instead, so nothing
     * here reaches `toThreadCommand` any more. The entry is kept only to hold
     * `/report`'s seat in the menu order.
     */
    {
      labelKey: 'threads.detail.command.report.label',
      blurbKey: 'threads.detail.command.report.blurb',
      confirmKey: 'threads.detail.command.report.confirm',
      startedKey: 'threads.detail.command.report.started',
    },
  ],
  [
    'thread-action-slack',
    {
      labelKey: 'threads.detail.command.slack.label',
      blurbKey: 'threads.detail.command.slack.blurb',
      confirmKey: 'threads.detail.command.slack.confirm',
      startedKey: 'threads.detail.command.slack.started',
    },
  ],
])

/**
 * `/report`'s own slug, which is NOT a template slug.
 *
 * Report mode is an assistant turn (`docs/plans/2026-09-03-assistant-report-mode.md`,
 * D1), so the command carries no `thread-action-` prefix: an armed `/report`
 * must not resolve through `useWorkflowTemplate` and must not be startable as
 * a run. `commandKey` still reduces it to `report`, so every `data-testid` the
 * shipped tests point at is unchanged.
 */
export const REPORT_COMMAND_SLUG = 'report'

/**
 * The seeded template `/report` used to come from. Filtered out of the list
 * here so the native command is the only `/report` row while the row still
 * exists in the catalogue; Step 7 of the plan retires the row itself.
 */
export const RETIRED_REPORT_TEMPLATE_SLUG = 'thread-action-report'

/**
 * The native `/report` command: arms a report-kind intake, collects nothing,
 * and never resolves a template. Built per call rather than as a module
 * constant because every string is i18n, and the list is rebuilt when the
 * language changes.
 */
function nativeReportCommand(): ThreadCommand {
  const blurb = i18n.t('threads.detail.command.report.blurb', { ns: 'app' })
  return {
    slug: REPORT_COMMAND_SLUG,
    token: '',
    command: '/report',
    label: i18n.t('threads.detail.command.report.label', { ns: 'app' }),
    blurb,
    confirmLabel: i18n.t('threads.detail.command.report.confirm', { ns: 'app' }),
    prompt: blurb,
    startedMessage: i18n.t('threads.detail.command.report.started', {
      ns: 'app',
    }),
    /* Arms, like `/test`: the scoping beat comes first, the writing second,
       and the armed send goes to intake rather than to a run. */
    arms: true,
    intake: true,
    kind: 'report',
    intakeKind: 'report',
  }
}

/** `thread-action-report` → `/report`. The whole of D1's derivation. */
export function deriveCommandName(slug: string): string {
  if (!slug.startsWith(THREAD_ACTION_SLUG_PREFIX)) {
    return `/${slug}`
  }
  return `/${slug.slice(THREAD_ACTION_SLUG_PREFIX.length)}`
}

/** Alias override (`/test`, `/copywriting`) when present, otherwise the derived `/name`. */
export function commandNameForSlug(slug: string): string {
  return THREAD_COMMAND_ALIASES.get(slug)?.command ?? deriveCommandName(slug)
}

/** True when `typed` is the command or one of its older spellings. */
export function matchesTypedCommand(
  entry: { command: string; legacyCommands?: Array<string> },
  typed: string,
): boolean {
  const needle = typed.toLowerCase()
  if (entry.command.toLowerCase() === needle) {
    return true
  }
  return (entry.legacyCommands ?? []).some((name) => name.toLowerCase() === needle)
}

function toThreadCommand(summary: WorkflowTemplateSummary): ThreadCommand {
  const alias = THREAD_COMMAND_ALIASES.get(summary.slug)
  const command = alias?.command ?? deriveCommandName(summary.slug)

  /* Fallbacks (D1a) for a template nobody wrote copy for: the catalogue's own
     metadata, and a sentence built from the name. Worse than curated copy, but
     it renders, which is the point of deriving the list at all. */
  const label = alias
    ? i18n.t(alias.labelKey, { ns: 'app' })
    : summary.displayName

  return {
    slug: summary.slug,
    token: summary.token,
    command,
    ...(alias?.legacyCommands?.length
      ? { legacyCommands: alias.legacyCommands }
      : {}),
    label,
    blurb: alias
      ? alias.blurbKey
        ? i18n.t(alias.blurbKey, { ns: 'app' })
        : ''
      : summary.description,
    confirmLabel: alias
      ? alias.confirmNs === 'common'
        ? i18n.t(alias.confirmKey as ParseKeys<'common'>, { ns: 'common' })
        : i18n.t(alias.confirmKey as ParseKeys<'app'>, { ns: 'app' })
      : i18n.t('threads.detail.command.runNamed', { command, ns: 'app' }),
    prompt: summary.description,
    startedMessage: alias
      ? i18n.t(alias.startedKey, { ns: 'app' })
      : i18n.t('threads.detail.command.startedNamed', { name: label, ns: 'app' }),
    arms: alias?.arms ?? false,
    /* Everything derived from a template starts a run; only the native
       `/report` above is report-kind. */
    kind: 'workflow',
    ...(alias?.intake ? { intake: true } : {}),
  }
}

export interface UseThreadCommandsResult {
  commands: Array<ThreadCommand>
  isLoading: boolean
}

export interface UseThreadCommandsOptions {
  /**
   * D5: `'off-thread'` narrows the list to aliases flagged `offThread: true`
   * (today `/test`) for a composer that has not minted a thread yet — the
   * only command an off-thread composer can hand off, since anything else
   * would need a thread to run in. `'thread'` (the default, so an existing
   * caller that never passes options keeps its unfiltered list) is every
   * seeded command.
   */
  surface?: 'thread' | 'off-thread'
}

/**
 * The commands this thread can offer, derived from the seeded
 * `thread-action-*` templates rather than from a table in a component.
 *
 * SWR-backed rather than `useEffect`-backed because two surfaces mount this at
 * once — the composer's typed `/` form and the `Commands` menu beside it — and
 * two raw effects would issue `GET /workflows` and the Slack-webhook read twice
 * per thread visit, with two copies of state that can disagree. Both keys are
 * null while `enabled` is false, so a page with no thread (or with the feature
 * flag off) fetches nothing at all.
 *
 * The template list is deliberately NOT shared with `useWorkflowTemplates`:
 * `GET /workflows` hides the `thread-action-*` slugs from the lobby by default,
 * this asks for them explicitly with the flag, and the workflow menu must keep
 * showing the lobby's list and nothing else.
 *
 * A failed template read yields no TEMPLATE-DERIVED commands and says nothing.
 * There is no command to offer and no decision for the reader to make, so a
 * toast here would only be noise on a page whose real content loaded fine.
 *
 * The native `/report` is the exception, and deliberately so (Step 6): it has
 * no template record to lose, so a `GET /workflows` outage must not make it
 * vanish from the menu, toast, or disarm a report intake already in flight.
 * It is built before the template read is consulted at all.
 */
export function useThreadCommands(
  enabled: boolean,
  options: UseThreadCommandsOptions = {},
): UseThreadCommandsResult {
  const surface = options.surface ?? 'thread'
  const { data: templates, isLoading } = useSWR(
    enabled ? ['workflow-templates', 'thread-actions'] : null,
    () => workflowsApi.listTemplates(false, true),
  )

  /**
   * Slack webhook presence, from the settings endpoint's `configured` boolean.
   * FAIL OPEN: an unanswered or failed read keeps the command VISIBLE, because
   * a settings request that hiccups is not evidence the org has no webhook.
   * Only a definite `false` hides it.
   */
  const { data: slackSettings } = useSWR(
    enabled ? ['workflow-delivery', 'slack'] : null,
    () => workflowDeliverySettingsApi.getSlackWebhook(),
  )
  const isSlackConfigured = slackSettings?.configured ?? null

  const commands = useMemo(() => {
    /* Disabled means no thread and no assistant on this surface at all, so
       there is nothing to offer — the native `/report` included, since it
       needs a thread to report on. Kept ahead of everything else so a hook
       that fetches nothing also lists nothing. */
    if (!enabled) {
      return []
    }

    const aliasOrder = Array.from(THREAD_COMMAND_ALIASES.keys())
    function rank(slug: string): number {
      const index = aliasOrder.indexOf(slug)
      return index === -1 ? aliasOrder.length : index
    }

    /* The native `/report`, built BEFORE the template read is consulted.
       It keeps the seat its retired template used to hold, so a reader who
       learned the menu order does not have to relearn it. Thread surface
       only: an off-thread composer has no thread to report on, and D5
       already narrows that surface to `/test`. */
    const reportEntry =
      surface === 'off-thread'
        ? null
        : {
            command: nativeReportCommand(),
            rank: rank(RETIRED_REPORT_TEMPLATE_SLUG),
            index: -1,
          }

    /* A failed or still-empty template read costs the TEMPLATE commands and
       nothing else. `/report` has no template record behind it, so a
       `GET /workflows` outage may not take it down with them. */
    if (!templates) {
      return reportEntry ? [reportEntry.command] : []
    }

    const threadActions = templates.filter(
      (summary) => templateSurface(summary) === 'thread',
    )
    const visible = threadActions.filter(
      (summary) =>
        summary.slug !== 'thread-action-slack' || isSlackConfigured !== false,
    )
    /* D10: `/report` is native now, so the seeded template it used to come
       from is dropped rather than listed beside its replacement. Harmless once
       Step 7 deactivates the row; required until then. */
    const withoutRetiredReport = visible.filter(
      (summary) => summary.slug !== RETIRED_REPORT_TEMPLATE_SLUG,
    )

    /* D5: off-thread only offers a command an off-thread composer can hand
       off to the thread page it is about to mint. A slug the frontend has
       never heard of has no alias entry, so it stays thread-only rather than
       silently widening the off-thread surface every time a new
       `thread-action-*` template gets seeded. */
    const surfaced =
      surface === 'off-thread'
        ? withoutRetiredReport.filter(
            (summary) => THREAD_COMMAND_ALIASES.get(summary.slug)?.offThread === true,
          )
        : withoutRetiredReport

    /* Stable within each rank, so unaliased templates keep the response's own
       order rather than being sorted by a field nobody chose. */
    const ordered = surfaced.map((summary, index) => ({
      command: toThreadCommand(summary),
      rank: rank(summary.slug),
      index,
    }))

    if (reportEntry) {
      ordered.push(reportEntry)
    }

    return ordered
      .sort((a, b) => a.rank - b.rank || a.index - b.index)
      .map((entry) => entry.command)
  }, [enabled, templates, isSlackConfigured, i18n.language, surface])

  return {
    commands,
    /* The webhook read is deliberately NOT part of this: it can only ever
       remove one row and it fails open, so holding the whole list back for it
       would delay every command for a call none of them depend on. */
    isLoading,
  }
}
